import { useState } from 'react';
import type { FormEvent } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const SUBMIT_URL = 'https://readdy.ai/api/form/dbb65r6ctmbk4sp5cc40';

const values = [
  {
    icon: 'ri-quill-pen-line',
    title: '编辑式策展',
    desc: '不以流量为唯一标准。每个专题、每篇资讯都会经过人工筛选与整理，保证推荐有理由。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '信息结构化',
    desc: '影片的类型、年份、地区、评分、主创信息尽量完整呈现，方便你快速判断是否值得看。',
  },
  {
    icon: 'ri-moon-clear-line',
    title: '为深夜而生',
    desc: '我们相信深夜是看电影最好的时间。深色界面、克制的动效，只为了让屏幕成为房间里唯一的光。',
  },
];

export default function AboutPage() {
  useDocumentMeta({
    title: '关于我们 - 午夜电影网',
    description:
      '了解午夜电影网的定位、策展理念与联系方式。我们是一个以高清电影推荐与影视资讯为核心的演示型观影门户。',
    keywords: '午夜电影网,关于我们,电影网站,影视资讯,联系我们',
  });

  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [formError, setFormError] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setSubmitState('success');
      form.reset();
      setMessageLength(0);
      return;
    }

    const message = String(formData.get('message') ?? '').trim();
    if (!message) {
      setSubmitState('error');
      setFormError('请填写留言内容后再提交。');
      return;
    }
    if (message.length > 500) {
      setSubmitState('error');
      setFormError('留言内容不能超过 500 个字符，请精简后再提交。');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value !== 'string') return;
      const trimmed = value.trim();
      if (trimmed) payload.append(key, trimmed);
    });

    setSubmitState('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const code = parsed?.code;
      const isSpam = typeof serverMessage === 'string' && serverMessage.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setSubmitState('success');
        form.reset();
        setMessageLength(0);
        return;
      }

      setSubmitState('error');
      setFormError(
        isSpam
          ? '提交被系统拦截，请确认内容后重试。'
          : serverMessage || '提交失败，请稍后再试，或通过邮箱与我们联系。',
      );
    } catch {
      setSubmitState('error');
      setFormError('网络异常，留言没有发送成功，请检查网络后重试。');
    }
  };

  return (
    <div>
      <section className="relative overflow-hidden border-b border-background-200 bg-accent-100 pb-10 pt-24 md:pb-14 md:pt-32">
        <div className="absolute -right-20 top-4 h-72 w-72 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="shell relative">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <div className="mt-5 max-w-3xl">
            <p className="eyebrow text-[11px] text-primary-500">About Midnight Cinema</p>
            <h1 className="mt-2 font-heading text-3xl font-semibold tracking-wide text-foreground-950 md:text-5xl">
              关于午夜电影网
            </h1>
            <p className="mt-3 text-sm leading-8 text-foreground-500 md:text-base">
              我们想做的不是一个塞满资源的仓库，而是一间灯关着的小放映厅：片单是有人挑过的，资讯是有人读完的，推荐是有理由的。
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="shell grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <h2 className="flex items-center gap-2 font-heading text-2xl tracking-wide text-foreground-950 md:text-3xl">
              <span className="h-4 w-1 rounded-full bg-primary-500" />
              我们是谁
            </h2>
            <div className="mt-4 space-y-4 text-sm leading-8 text-foreground-600">
              <p>
                午夜电影网是一个以高清电影推荐与影视资讯为核心的观影门户。站内的影片资料覆盖动作、科幻、爱情、喜剧、剧情、悬疑、犯罪等多个类型，并按年份、地区、评分做了结构化整理，方便你按自己的口味筛选。
              </p>
              <p>
                除了片库，我们还有一个持续更新的资讯频道，内容包含影评、幕后揭秘、奖项观察和行业动态。我们更关心一部电影为什么被拍出来，而不只是它卖了多少票房。
              </p>
              <p>
                需要说明的是：这是一个前端演示站点。所有影片、评分、榜单、资讯与专题内容均为示例数据，播放按钮仅展示交互流程，不提供真实片源、播放地址或下载服务，也不涉及账号、会员与支付功能。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                { value: '18', label: '精选影片' },
                { value: '9', label: '深度资讯' },
                { value: '6', label: '主题专题' },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100 p-4 text-center">
                  <p className="font-heading text-2xl text-primary-500 md:text-3xl">{stat.value}</p>
                  <p className="mt-1 text-[11px] text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-xl tracking-wide text-foreground-950">联系方式</h2>
              <ul className="mt-4 space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-200 text-primary-500">
                    <i className="ri-mail-line" aria-hidden="true"></i>
                  </span>
                  <span>
                    <span className="block text-foreground-500">内容合作与纠错</span>
                    <span className="mt-0.5 block text-foreground-800">hello@midnightcinema.demo</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-200 text-primary-500">
                    <i className="ri-time-line" aria-hidden="true"></i>
                  </span>
                  <span>
                    <span className="block text-foreground-500">编辑在线时间</span>
                    <span className="mt-0.5 block text-foreground-800">每晚 20:00 — 02:00</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-200 text-primary-500">
                    <i className="ri-map-pin-line" aria-hidden="true"></i>
                  </span>
                  <span>
                    <span className="block text-foreground-500">编辑部（演示地址）</span>
                    <span className="mt-0.5 block text-foreground-800">线上协作 · 不设实体门店</span>
                  </span>
                </li>
              </ul>
              <p className="mt-5 rounded-md border border-primary-500/25 bg-primary-500/10 p-3 text-[11px] leading-6 text-foreground-500">
                本站为演示项目，联系方式与办公信息均为示例，请勿用于真实业务沟通。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-200 bg-background-100 py-12 md:py-16">
        <div className="shell">
          <h2 className="font-heading text-2xl tracking-wide text-foreground-950 md:text-3xl">我们在意的事</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {values.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500/10 text-primary-500">
                  <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 text-sm font-medium text-foreground-900">{item.title}</h3>
                <p className="mt-2 text-xs leading-6 text-foreground-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="shell grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="eyebrow text-[11px] text-primary-500">Leave a message</p>
            <h2 className="mt-2 font-heading text-2xl tracking-wide text-foreground-950 md:text-3xl">给我们留言</h2>
            <p className="mt-3 text-sm leading-7 text-foreground-500">
              发现影片信息有误、想推荐一部电影，或者只是想聊聊某个结局？都欢迎写下来。我们会认真读完每一条留言。
            </p>
            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
              <p className="flex items-start gap-2 text-xs leading-6 text-foreground-500">
                <i className="ri-information-line mt-1 text-primary-500" aria-hidden="true"></i>
                留言会通过站内表单服务提交，不使用账号体系，也不会展示在页面上。提交后请以页面提示为准。
              </p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
              <form
                id="about-message-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
                noValidate={false}
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="min-w-0">
                    <label htmlFor="contact-name" className="mb-1.5 block text-xs text-foreground-600">
                      你的称呼 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      maxLength={40}
                      placeholder="怎么称呼你"
                      className="w-full min-w-0 box-border rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500/70 focus:outline-none"
                    />
                  </div>
                  <div className="min-w-0">
                    <label htmlFor="contact-email" className="mb-1.5 block text-xs text-foreground-600">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收回复"
                      className="w-full min-w-0 box-border rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500/70 focus:outline-none"
                    />
                  </div>
                </div>

                <div className="min-w-0">
                  <label htmlFor="contact-topic" className="mb-1.5 block text-xs text-foreground-600">
                    留言主题
                  </label>
                  <div className="relative">
                    <select
                      id="contact-topic"
                      name="topic"
                      defaultValue="内容建议"
                      className="w-full min-w-0 appearance-none rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 pr-9 text-sm text-foreground-900 focus:border-primary-500/70 focus:outline-none"
                    >
                      <option value="内容建议">内容建议</option>
                      <option value="影片纠错">影片纠错</option>
                      <option value="专题推荐">专题推荐</option>
                      <option value="合作咨询">合作咨询</option>
                      <option value="其他">其他</option>
                    </select>
                    <i
                      className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-foreground-500"
                      aria-hidden="true"
                    ></i>
                  </div>
                </div>

                <div className="min-w-0">
                  <label htmlFor="contact-message" className="mb-1.5 block text-xs text-foreground-600">
                    留言内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    onChange={(event) => setMessageLength(event.target.value.length)}
                    placeholder="想说的话写在这里，最多 500 个字符"
                    className="w-full min-w-0 box-border resize-none rounded-md border border-background-300/70 bg-background-50 px-3 py-2.5 text-sm leading-6 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500/70 focus:outline-none"
                  />
                  <div className="mt-1.5 flex items-center justify-between text-[11px] text-foreground-500">
                    <span>请勿填写敏感个人信息</span>
                    <span className={messageLength > 480 ? 'text-primary-500' : ''}>{messageLength} / 500</span>
                  </div>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="field-optional-assist"
                />

                {submitState === 'error' && (
                  <div className="flex items-start gap-2 rounded-md border border-primary-500/40 bg-primary-500/10 px-3 py-2.5 text-xs leading-6 text-primary-500">
                    <i className="ri-error-warning-line mt-0.5" aria-hidden="true"></i>
                    <span>{formError}</span>
                  </div>
                )}

                {submitState === 'success' && (
                  <div className="flex items-start gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-xs leading-6 text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" aria-hidden="true"></i>
                    <span>留言已提交，谢谢你花时间写下这些。我们会尽快查看。</span>
                  </div>
                )}

                <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-[11px] text-foreground-500">提交即表示你了解本站为演示项目。</p>
                  <button
                    type="submit"
                    disabled={submitState === 'submitting'}
                    className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <i className={submitState === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} aria-hidden="true"></i>
                    {submitState === 'submitting' ? '提交中…' : '提交留言'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="shell">
          <div className="rounded-lg border border-background-200 bg-background-100 px-6 py-8 md:px-10">
            <h2 className="font-heading text-xl tracking-wide text-foreground-950">版权与声明</h2>
            <div className="mt-3 space-y-2 text-xs leading-7 text-foreground-500">
              <p>© 2026 午夜电影网 Midnight Cinema. 本站为演示项目，保留所有权利。</p>
              <p>站内所有影片名称、海报、评分、榜单、资讯与专题内容均为演示用途，不构成任何真实影视资源服务。</p>
              <p>本站不提供下载、不提供在线播放片源、不涉及会员、支付与账号功能。如需反馈内容问题，可使用上方留言表单。</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}